import { useCallback, useEffect, useMemo, useState } from "react";

import { ModifierSetDto } from "services/modifierSets/restModels";
import { ModifierSetRequestDto } from "services/modifierSets/restRequests";
import { useConstants } from "shared/hooks/useConstants";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useLocale } from "shared/hooks/useLocale";
import { useModifierSets } from "shared/hooks/useModifierSets";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { useNotificationSnackbar } from "shared/ui";

import { useModifierSetMutations } from "features/ModifierSets/hooks/useModifierSetMutations";
import { ModifierSetsState } from "features/ModifierSets/types";

// initialModifierSetId — набор из адреса страницы: открываем его диалог сразу после загрузки.
export const useModifierSetsState = (initialModifierSetId?: number): ModifierSetsState => {
    const locale = useLocale();
    const currencyId = useCurrencyId();

    const {
        modifierSets,
        loading: modifierSetsLoading,
        error: modifierSetsError,
        setModifierSets,
    } = useModifierSets();
    const {
        constants,
        loading: constantsLoading,
        error: constantsError,
    } = useConstants();

    const notify = useNotificationSnackbar();
    const { saving, saveModifierSet, removeModifierSet } = useModifierSetMutations(
        modifierSets,
        setModifierSets,
        notify
    );

    const [isSetDialogOpen, setIsSetDialogOpen] = useState<boolean>(false);
    const [editedModifierSet, setEditedModifierSet] = useState<ModifierSetDto | null>(null);
    const [modifierSetToDelete, setModifierSetToDelete] = useState<ModifierSetDto | null>(null);
    const [isInitialSetOpened, setIsInitialSetOpened] = useState<boolean>(false);

    const currency = constants?.currencies.find((item) => item.id === currencyId) || null;
    const currencyFormatter = useMemo(
        () => createCurrencyFormatter(currency, locale),
        [currency, locale]
    );

    // Основной язык кабинета — первым, чтобы переключатель открывался на нём.
    const availableLanguages = useMemo(() => {
        const locales = (constants?.locales || []).map((item) => item.id);
        return [locale, ...locales.filter((item) => item !== locale)];
    }, [constants, locale]);

    useEffect(() => {
        if (isInitialSetOpened || !initialModifierSetId || modifierSetsLoading) return;

        const initialSet = modifierSets.find((item) => item.id === initialModifierSetId);

        setIsInitialSetOpened(true);

        if (initialSet) {
            setEditedModifierSet(initialSet);
            setIsSetDialogOpen(true);
        }
    }, [initialModifierSetId, isInitialSetOpened, modifierSets, modifierSetsLoading]);

    const openCreateDialog = useCallback(() => {
        setEditedModifierSet(null);
        setIsSetDialogOpen(true);
    }, []);

    const openEditDialog = useCallback((modifierSet: ModifierSetDto) => {
        setEditedModifierSet(modifierSet);
        setIsSetDialogOpen(true);
    }, []);

    const closeSetDialog = useCallback(() => {
        setIsSetDialogOpen(false);
        setEditedModifierSet(null);
    }, []);

    const submitModifierSet = useCallback(
        async (values: ModifierSetRequestDto): Promise<boolean> => {
            const saved = await saveModifierSet(values, editedModifierSet ? editedModifierSet.id : null);

            if (!saved) return false;

            closeSetDialog();
            return true;
        },
        [closeSetDialog, editedModifierSet, saveModifierSet]
    );

    const openDeleteDialog = useCallback(
        (modifierSet: ModifierSetDto) => setModifierSetToDelete(modifierSet),
        []
    );

    const closeDeleteDialog = useCallback(() => setModifierSetToDelete(null), []);

    const confirmDelete = useCallback(async () => {
        if (!modifierSetToDelete) return;

        const remaining = await removeModifierSet(modifierSetToDelete.id);

        if (remaining) setModifierSetToDelete(null);
    }, [modifierSetToDelete, removeModifierSet]);

    return {
        modifierSets,
        // Константы нужны для валюты и переключателя языков — гейтим страницу и на них.
        loading: modifierSetsLoading || constantsLoading,
        error: modifierSetsError || constantsError,
        saving,

        locale,
        availableLanguages,
        currencyFormatter,

        editedModifierSet,
        isSetDialogOpen,
        openCreateDialog,
        openEditDialog,
        closeSetDialog,
        submitModifierSet,

        notify,

        modifierSetToDelete,
        openDeleteDialog,
        closeDeleteDialog,
        confirmDelete,
    };
};
